import {CommonModule} from '@angular/common';
import {Component, Injector, OnInit} from '@angular/core';
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Router, RouterModule} from '@angular/router';
import {NgxPaginationModule} from 'ngx-pagination';
import {Subject, takeUntil} from 'rxjs';
import {extractErrorMessage, goBack} from 'src/app/common/common';
import {CreateMetadataReport} from 'src/app/model/metadataReports.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import {SearchFilterModule} from '../../../common/searchFilter/searchFilter.module';
import {CookieService} from 'ngx-cookie-service';
import { RidViewModel } from '../../../../model/rids.model';

type RidListElement = { id: string; };

@Component({
  selector: 'app-create-metadata-report-index',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, NgxPaginationModule, SearchFilterModule],
  templateUrl: './create-metadata-report.component.html',
  styleUrl: './create-metadata-report.component.scss'
})
export class CreateMetadataReportComponent implements OnInit {
  public username: string;
  public error: string | null = null;

  // Pagination & Search
  ridsSelectedPagination: any;
  searchTextGroupSelected: string = '';
  possibleRidsSelectedPagination: any;
  possibleSearchTextGroupSelected: string = '';

  // forms control
  metadataReportForm = new FormGroup({
    report_specifier_id: new FormControl<string>(''),
    label: new FormControl<string>(''),
    dataSource: new FormControl<string>(''),
    reportType: new FormControl<string>(''),
    reportOffset: new FormControl<string>(''),
    backfillLabel: new FormControl<string>(''), // Added from Venway2
    includeReportRidPrefix: new FormControl<boolean>(false),
    sendUpdates: new FormControl<boolean>(false),
    updateType: new FormControl<string>(''),
    updatedby: new FormControl<string>(''),
    scheduledUpdateTime: new FormControl<string>(''),
    scheduledUpdateDuration:  new FormControl<string>(''),
    reportStyle:  new FormControl<string>(''),
  });

  unsubscribe = new Subject<void>();
  formsSubscribe: any;

  public editing: boolean = false;
  public metadataId: string;
  public router: Router;
  protected metadataReport: CreateMetadataReport = {
    data_source: '',
    report_type: '',
    report_specifier_id: '',
    label: '',
    created_at: '',
    rids: [],
    id: 0,
    report_style: 'INTERVAL_START',
    report_offset: '',
    backfill_label: '',
    include_report_rid_prefix: false,
    update_type: '',
    scheduled_update_time: '',
    scheduled_update_duration: '',
    send_updates: false,
  }
  protected displayedPossibleRids: RidListElement[] = [];
  protected displayedIncludedRids: RidListElement[] = [];

  constructor(private injector: Injector, private apiService: ApiService, private cookieService: CookieService){
    const loginInfo = JSON.parse(this.cookieService.get('oadr_login'));
    this.username = loginInfo.username;
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.metadataId = event.snapshot.params.id;
      }
    });

    this.ridsSelectedPagination = {
      currentPage: 1,
      itemsPerPage: 6,
      id: 'ridsSelectedPagination'
    };

    this.possibleRidsSelectedPagination = {
      currentPage: 1,
      itemsPerPage: 6,
      id: 'possibleRidsPagination'
    };
  }

  ngOnInit(): void {
    this.getMetadataReport();
    this.initForm();
  }

  // Helper function to validate ISO duration strings
  isValidIsoDuration(duration: string): boolean {
    if (!duration || duration.trim() === '') {
      return true; // null/empty is valid
    }

    const isoDurationPattern = /^P(?:(?:\d+(?:\.\d+)?Y)?(?:\d+(?:\.\d+)?M)?(?:\d+(?:\.\d+)?D)?)?(?:T(?:\d+(?:\.\d+)?H)?(?:\d+(?:\.\d+)?M)?(?:\d+(?:\.\d+)?S)?)?$/;
    return isoDurationPattern.test(duration.trim());
  }

  // Helper function to check if ISO duration represents zero time
  isZeroDuration(duration: string): boolean {
    if (!duration || duration.trim() === '') {
      return false; // null/empty is not considered zero, it's null
    }

    const zeroDurationPatterns = [
      /^PT0S$/i,           // PT0S
      /^PT0M$/i,           // PT0M
      /^PT0H$/i,           // PT0H
      /^P0D$/i,            // P0D
      /^PT0H0M0S$/i,       // PT0H0M0S
      /^P0DT0H0M0S$/i,     // P0DT0H0M0S
    ];

    return zeroDurationPatterns.some(pattern => pattern.test(duration.trim()));
  }

  setFormsDefaultValue(){
    this.metadataReportForm.controls.report_specifier_id.setValue(this.metadataReport.report_specifier_id)
    this.metadataReportForm.controls.label.setValue(this.metadataReport.label);
    this.metadataReportForm.controls.dataSource.patchValue(this.metadataReport.data_source)
    this.metadataReportForm.controls.reportType.setValue(this.metadataReport.report_type);
    this.metadataReportForm.controls.reportOffset.setValue(this.metadataReport.report_offset!);
    this.metadataReportForm.controls.backfillLabel.setValue(this.metadataReport.backfill_label!);
    this.metadataReportForm.controls.includeReportRidPrefix.setValue(this.metadataReport.include_report_rid_prefix);
    this.metadataReportForm.controls.sendUpdates.setValue(this.metadataReport.send_updates);
    this.metadataReportForm.controls.updateType.setValue(this.metadataReport.update_type);
    this.metadataReportForm.controls.scheduledUpdateTime.setValue(this.metadataReport.scheduled_update_time!);
    this.metadataReportForm.controls.scheduledUpdateDuration.setValue(this.metadataReport.scheduled_update_duration!);
    this.metadataReportForm.controls.reportStyle.setValue(this.metadataReport.report_style);
  }

  fetchRids() {
    const queryParams: CustomHttpParams = {
      'depth': 1,
      'data_source': this.metadataReport.data_source,
    };
    console.log('HEWYEHEH')
    console.log(this.metadataReport.data_source)
    if (this.metadataReport?.data_source) {
      this.apiService.getRids(queryParams).subscribe({
        next: (responseRids: RidViewModel[]) => {
          const metadataReportRidIds = this.metadataReport?.rids?.map((rid) => rid.id);
          this.displayedIncludedRids = metadataReportRidIds.map((id: string) => { return { id } });
          this.displayedPossibleRids = responseRids.filter((rid) =>
            !metadataReportRidIds.includes(rid.id)
          );
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err);
        }
      });
    } else {
      // If no data source is selected, clear both lists
      this.displayedPossibleRids = [];
      this.displayedIncludedRids = [];
    }
  }

  getMetadataReport() {
    if (this.metadataId) {
      const queryParams: CustomHttpParams = {
        'depth': 2,
        'id': this.metadataId,
      };
      this.apiService.getMetadataReports(queryParams)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (response) => {
            if (response && response.length) {
              const reportFound = response.find((metadataReport) => metadataReport.id === Number(this.metadataId));
              if (reportFound) {
                this.metadataReport = {...reportFound};
                this.setFormsDefaultValue();
                this.editing = true;
                return;
              }
            }
          },
          error: (err) => {
            console.error(err);
            this.error = extractErrorMessage(err);
          }
        });
    }
    //if something isn't ok
    this.resetValues();
  }

  private resetValues() {
    this.metadataReport.send_updates = false;
    this.metadataReport.rids = [];
    this.metadataReport.report_style = 'INTERVAL_START';
    this.displayedIncludedRids = [];
    this.displayedPossibleRids = [];
    this.editing = false;
  }

  initForm() {
    this.formsSubscribe = this.metadataReportForm.controls.report_specifier_id.valueChanges.subscribe(
      (val: any) => this.metadataReport.report_specifier_id = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.label.valueChanges.subscribe(
      (val: any) => this.metadataReport.label = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.dataSource.valueChanges.subscribe(
      (val: any) => {
        this.metadataReport.data_source = val;
        this.fetchRids();
      }
    );

    this.formsSubscribe = this.metadataReportForm.controls.reportType.valueChanges.subscribe(
      (val: any) => this.metadataReport.report_type = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.reportOffset.valueChanges.subscribe(
      (val: any) => this.metadataReport.report_offset = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.backfillLabel.valueChanges.subscribe(
      (val: any) => this.metadataReport.backfill_label = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.includeReportRidPrefix.valueChanges.subscribe(
      (val: any) => this.metadataReport.include_report_rid_prefix = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.sendUpdates.valueChanges.subscribe(
      (val: any) => this.metadataReport.send_updates = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.updateType.valueChanges.subscribe(
      (val: any) => this.metadataReport.update_type = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.scheduledUpdateTime.valueChanges.subscribe(
      (val: any) => this.metadataReport.scheduled_update_time = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.scheduledUpdateDuration.valueChanges.subscribe(
      (val: any) => this.metadataReport.scheduled_update_duration = val
    );

    this.formsSubscribe = this.metadataReportForm.controls.reportStyle.valueChanges.subscribe(
      (val: any) => this.metadataReport.report_style = val
    );
  }

  save(){
    // Form validation
    if (!this.metadataReport.report_specifier_id ||
      !this.metadataReport.data_source ||
      !this.metadataReport.report_type ||
      (this.metadataReport.data_source === 'x-energy' && !this.metadataReport.report_style)) {
      this.error = 'Please fill out all fields correctly and try again.';
      return;
    }

    // Validate the report_offset if it's provided
    if (this.metadataReport.report_offset) {
      if (!this.isValidIsoDuration(this.metadataReport.report_offset)) {
        this.error = 'Report offset must be a valid ISO duration (e.g., "PT3H", "PT0S", "PT30M").';
        return;
      }
    }

    // Convert empty string to 'PT0S' for zero offset (similar to Venway2 behavior)
    if (this.metadataReport.report_offset === '') {
      this.metadataReport.report_offset = 'PT0S';
    }

    // Extract IDs from RIDs array
    this.metadataReport.rids = this.metadataReport.rids.map((rid: any) => rid.id);

    if(!this.metadataReport.report_offset) {
      delete this.metadataReport.report_offset;
    }

    if(!this.metadataReport.backfill_label) {
      delete this.metadataReport.backfill_label;
    }

    if(this.metadataReport.update_type === 'scheduled' || !this.metadataReport.include_report_rid_prefix) {
      delete this.metadataReport.scheduled_update_duration;
    }

    if(this.metadataReport.update_type === 'window' || !this.metadataReport.send_updates) {
      delete this.metadataReport.scheduled_update_time;
    }

    if (this.editing) {
      this.apiService.editMetadataReport(this.metadataReport).subscribe({
        next: () => goBack('/metadataReports', this.router),
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err);
        }
      });
    } else {
      delete this.metadataReport.updated_by;
      delete this.metadataReport.id;
      this.apiService.createMetadataReport(this.metadataReport).subscribe({
        next: () => goBack('/metadataReports', this.router),
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err);
        }
      });
    }
  }

  removeRid(rid: any){
    this.displayedPossibleRids.push(rid);
    this.metadataReport.rids = this.metadataReport.rids.filter((r: any) => r.id !== rid.id);
    this.displayedIncludedRids = this.metadataReport.rids;
  }

  includeRid(rid: any){
    this.metadataReport.rids.push(rid);
    this.displayedPossibleRids = this.displayedPossibleRids.filter((r: any) => r.id !== rid.id);
    this.displayedIncludedRids = this.metadataReport.rids;
  }

  backPage(url: string, router: Router){
    goBack(url, router);
  }

  handlePageChange(event: any, page: any) {
    page.currentPage = event;
  }
}
